import { useEffect, useState } from 'react';
import React from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ProgressModal from '../components/ProgressModal';
import AttributionVisualizer from '../components/AttributionVisualizer';
import SequenceControls from '../components/SequenceControls';
import { Map } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    grade: '',
    status: '',
    search: '',
    source: 'all' // 'all', 'legacy', 'discovery'
  });
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [progressJobId, setProgressJobId] = useState(null);
  const [progressJobType, setProgressJobType] = useState(null);
  const [selectedLeadForAttribution, setSelectedLeadForAttribution] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, [page, filters]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await axios.get(`${API_BASE}/leads?${params}`);
      setLeads(response.data.leads);
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setPage(1);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv' || file.name.endsWith('.csv')) {
      setImportFile(file);
    } else {
      alert('Please select a CSV file');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Please select a CSV file first');
      return;
    }

    try {
      // Read file content
      const reader = new FileReader();
      reader.onload = async (e) => {
        const csvData = e.target.result;
        
        // Send to backend
        const response = await axios.post(
          `${API_BASE}/leads/import`,
          { csvData },
          { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' } }
        );

        // Show progress modal
        setProgressJobId(response.data.jobId);
        setProgressJobType('csv-import');
        setShowImport(false);
        setImportFile(null);
      };
      reader.readAsText(importFile);
    } catch (error) {
      console.error('Error importing CSV:', error);
      alert('Error importing CSV: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleProgressComplete = () => {
    setProgressJobId(null);
    setProgressJobType(null);
    // Refresh leads
    fetchLeads();
    setTimeout(() => fetchLeads(), 2000);
  };

  const handleProgressClose = () => {
    setProgressJobId(null);
    setProgressJobType(null);
  };

  return (
    <div>
      {progressJobId && (
        <ProgressModal
          jobId={progressJobId}
          jobType={progressJobType}
          onComplete={handleProgressComplete}
          onClose={handleProgressClose}
        />
      )}

      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Leads</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Import CSV
          </button>
          <button
            onClick={() => fetchLeads()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {showImport && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Import Leads from CSV</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select CSV File
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
              {importFile && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {importFile.name} ({(importFile.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-800">
                <strong>Expected CSV columns:</strong> first_name, last_name, email, company (or company_name), 
                job_title, technologies (or tech stack columns), and any custom fields.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleImport}
                disabled={!importFile}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Import Now
              </button>
              <button
                onClick={() => {
                  setShowImport(false);
                  setImportFile(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="all">All Sources</option>
              <option value="discovery">Discovery System (New)</option>
              <option value="legacy">Legacy Leads</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Name, email, company..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
            <select
              value={filters.grade}
              onChange={(e) => handleFilterChange('grade', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Grades</option>
              <option value="A+">A+</option>
              <option value="A">A</option>
              <option value="B">B</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="in_campaign">In Campaign</option>
              <option value="contacted">Contacted</option>
              <option value="converted">Converted</option>
              <option value="bounced">Bounced</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilters({ grade: '', status: '', search: '', source: 'all' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No leads found</div>
        ) : (
          <>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sequence</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Form</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leads.map((lead) => (
                  <React.Fragment key={lead.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {lead.first_name || lead.last_name ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lead.email || '-'}
                      {lead.email_verified && (
                        <span className="ml-2 px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded" title="Email verified">✓</span>
                      )}
                      {lead.email_predicted && !lead.email_verified && (
                        <span className="ml-2 px-1.5 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded" title="Email predicted (not verified)">?</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lead.company_name || '-'}
                      {lead.lead_source === 'discovery' && (
                        <span className="ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded" title="From Discovery System">New</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lead.job_title || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {lead.grade && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          lead.grade === 'A+' ? 'bg-green-100 text-green-800' :
                          lead.grade === 'A' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {lead.grade}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lead.score || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        lead.status === 'new' ? 'bg-gray-100 text-gray-800' :
                        lead.status === 'in_campaign' ? 'bg-blue-100 text-blue-800' :
                        lead.status === 'converted' ? 'bg-green-100 text-green-800' :
                        lead.status === 'bounced' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <SequenceControls leadId={lead.id} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {lead.landing_page_url ? (
                        <a
                          href={lead.landing_page_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                        >
                          Test Form
                        </a>
                      ) : (
                        <button
                          onClick={async () => {
                            try {
                              const response = await axios.post(`${API_BASE}/leads/${lead.id}/create-form`, {}, { headers: getAuthHeaders() });
                              alert(`Form created! URL: ${response.data.landing_page_url}`);
                              fetchLeads();
                            } catch (error) {
                              console.error('Error creating form:', error);
                              alert('Error creating form: ' + (error.response?.data?.message || error.message));
                            }
                          }}
                          className="text-green-600 hover:text-green-800 underline"
                        >
                          Create Form
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => setSelectedLeadForAttribution(selectedLeadForAttribution === lead.id ? null : lead.id)}
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <Map size={16} />
                        {selectedLeadForAttribution === lead.id ? 'Hide' : 'View'} Attribution
                      </button>
                    </td>
                  </tr>
                  {selectedLeadForAttribution === lead.id && (
                    <tr>
                      <td colSpan="10" className="px-6 py-4 bg-gray-50">
                        <AttributionVisualizer leadId={lead.id} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
